import Link from 'next/link';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';

import { BookLayoutEnum } from '~/api/book/bookApiTypes';
import { ArrowIcon } from '~/atomic/atom/icon/ArrowIcon';
import { SCTriangleArrow } from '~/atomic/organism/Header/Header';
import { ActiveLibraryEnum, headerActions } from '~/atomic/organism/Header/header.slice';
import { EmptyCatalogPage } from '~/atomic/page/catalog/EmptyCatalogPage';
import {
  SCCatalog,
  SCCatalogGrid, SCCatalogList,
  SCPaginationWrapper,
  SCSearchWord,
} from '~/atomic/page/catalog/styled';
import { getBooksWithLoading } from '~/atomic/page/catalog/tagPage/tagPage.data';
import { tagFiltersSelector, tagPageSeoSelector } from '~/atomic/page/catalog/tagPage/tagPage.selector';
import {
  PageTitleStyle, TextRegStyle, TextSmallStyle,
} from '~/atomic/Typography';
import { BookCard } from '~/feature/book/BookCard/BookCard';
import { BookListCard } from '~/feature/book/BookCard/BookListCard';
import { AuthModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/AuthModalWithSubscribeBook';
import { CallPhoneAuthModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/CallPhoneAuthModalWithSubscribeBook';
import { LoginModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/LoginModalWithSubscribeBook';
import { RegisterModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/RegisterModalWithSubscribeBook';
import { CatalogBookLayout } from '~/feature/catalog/CatalogBookLayout/CatalogBookLayout';
import { catalogBookLayoutSelector } from '~/feature/catalog/CatalogBookLayout/catalogBookLayout.selector';
import { CatalogSkeleton } from '~/feature/catalog/CatalogSkeleton';
import { CatalogFilter } from '~/feature/catalog/filter/CatalogFilter';
import { catalogFilterSelector } from '~/feature/catalog/filter/catalogFilter.selector';
import { isMobileSizeSelector } from '~/feature/media/windowSize.selector';
import { Pagination } from '~/feature/pagination/Pagination';
import { paginationSelector } from '~/feature/pagination/pagination.selector';
import { tagSelector } from '~/feature/tag/tag.selector';
import { userSelector } from '~/feature/user/user.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

export const TagPage: FC = () => {
  const dispatch = useAppDispatch();
  const { h1: h1Text } = useAppSelector(tagPageSeoSelector);

  const {
    books,
    isLoadBooks,
  } = useAppSelector(tagFiltersSelector);
  const {
    tags,
    tagSlug,
    isCanDownload,
    withAdvertisingFromBookriver,
  } = useAppSelector(catalogFilterSelector);
  const { isAuth } = useAppSelector(userSelector);
  const isMobileSize = useAppSelector(isMobileSizeSelector);
  const { currentTagSeo } = useAppSelector(tagSelector);
  const { layout } = useAppSelector(catalogBookLayoutSelector);
  const { total } = useAppSelector(paginationSelector);

  const loadBooks = () => {
    dispatch(getBooksWithLoading());
  };

  useEffect(() => {
    if (isAuth) {
      loadBooks();
    }
  }, [isAuth]);

  const openPopularTagsHandler = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (isMobileSize) {
      dispatch(headerActions.toggleIsHamburgerActive());
      dispatch(headerActions.changeActiveLibrary(ActiveLibraryEnum.Tag));
    } else {
      dispatch(headerActions.changeActiveLibrary(ActiveLibraryEnum.Tag));
      dispatch(headerActions.setIsOpenLibrary(true));
    }
  };

  const isHaveDefaultBooks = (books?.length ?? 0) > 0;

  const isHaveBooks = isHaveDefaultBooks;

  const isShowDefaultBooks = isHaveDefaultBooks && !isLoadBooks;
  const isShowEmptyCatalog = !isHaveBooks && !isLoadBooks;

  return (
    <>
      <SCCatalog>
        <SCSearchWordWrapper>
          <SCSubTitle
            onClick={openPopularTagsHandler}
          >
            Популярные темы
            <SCTriangleArrow />
          </SCSubTitle>
          <SCArrowIcon />
          <SCTitle>
            {h1Text}
          </SCTitle>
        </SCSearchWordWrapper>
        {tags?.length ? (
          <SCTags>
            {tags
              .filter((tag) => tag.slug !== tagSlug)
              .map((tag) => (
                <Link
                  key={tag.slug}
                  href={tag.url}
                  as={tag.url}
                  passHref
                  prefetch={false}
                >
                  <SCTag
                    data-is-active={tagSlug === tag.slug}
                  >
                    {tag.name}
                  </SCTag>
                </Link>
              ))}
          </SCTags>
        ) : null}
        {currentTagSeo?.seotext && (
          <SCDescription
            dangerouslySetInnerHTML={{ __html: currentTagSeo?.seotext }}
          />
        )}
        <CatalogFilter
          isShowAll
          onChange={loadBooks}
        />
        <CatalogBookLayout
          booksTotal={total}
        />
        <CatalogSkeleton isShow={isLoadBooks} />
        {isShowEmptyCatalog && (
          <EmptyCatalogPage />
        )}
        {isShowDefaultBooks && (
          <>
            {layout === BookLayoutEnum.GRID && (
              <SCCatalogGrid>
                {books.map((book) => (
                  <BookCard
                    isFb2={isCanDownload}
                    key={book.id}
                    book={book}
                    bookLinkQueries={withAdvertisingFromBookriver
                      ? {
                        ...book?.advertisingFromBookriver?.VMDAdCampaign?.utmQueryParams,
                        utm_content: 'tag',
                      }
                      : undefined}
                  />
                ))}
              </SCCatalogGrid>
            )}
            {layout === BookLayoutEnum.LIST && (
              <SCCatalogList>
                {books.map((book) => (
                  <BookListCard
                    isFb2={isCanDownload}
                    key={book.id}
                    book={book}
                    bookLinkQueries={withAdvertisingFromBookriver
                      ? {
                        ...book?.advertisingFromBookriver?.VMDAdCampaign?.utmQueryParams,
                        utm_content: 'tag',
                      }
                      : undefined}
                  />
                ))}
              </SCCatalogList>
            )}
          </>
        )}
        <SCPaginationWrapper>
          <Pagination onChange={loadBooks} />
        </SCPaginationWrapper>
      </SCCatalog>
      <AuthModalWithSubscribeBook />
      <LoginModalWithSubscribeBook />
      <RegisterModalWithSubscribeBook />
      <CallPhoneAuthModalWithSubscribeBook />
    </>
  );
};

const SCDescription = styled.div`
  ${TextRegStyle};
  color: var(--black-color);
  padding-bottom: 20px;
  text-align: left;
  align-self: flex-start;
`;

const SCSearchWordWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-top: 12px;
  margin-bottom: 20px;

  > *:not(:last-child) {
    margin-right: 10px;
  }

  ${lessThan('sm')} {
    display: inline;
  }
`;

const SCSubTitle = styled.span`
  ${PageTitleStyle};
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: var(--primary-hover-color);

    ${SCTriangleArrow} {
      background-color: var(--primary-hover-color);
    }
  }

  > * {
    margin-left: 4px;
  }
`;

const SCArrowIcon = styled(ArrowIcon)`
  font-size: 12px;
  color: var(--black-color);
`;

const SCTags = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  margin-bottom: 12px;

  > *:not(:last-child) {
    margin-right: 12px;
  }
`;

const SCTag = styled.a`
  ${TextSmallStyle};
  color: var(--black-color);

  &:hover {
    color: rgba(0, 0, 0, .65);
  }

  :first-letter {
    text-transform: uppercase;
  }
`;

const SCTitle = styled(SCSearchWord)`
  display: inline-block;
  margin-bottom: 0;
  font-weight: 400;

  :first-letter {
    text-transform: uppercase;
  }
`;
